import { IAPIRoutes } from "../../../../config";

export type ISelfSignupRepository = ReturnType<
  typeof SelfSignupRepositoryFactory
>;

export const SelfSignupRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  /**
   * Register the newly confirmed user in the backend.
   * Called once after cognitoConfirmSignUp succeeds.
   * Returns { user_id, activation_state: "pending_activation" }.
   */
  const selfSignup = async (payload: {
    first_name?: string;
    last_name?: string;
    phone?: string;
  }): Promise<{ user_id: string; activation_state: string }> =>
    POST(APIRoutes.apiSelfSignup, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

  /**
   * Poll this to find out when care-team approval is granted.
   * No patient_id required — works for pending users without a clinic_patient row.
   * Returns { user_id, activation_state: "pending_activation" | "active" }.
   */
  const checkActivationStatus = async (): Promise<{
    user_id: string;
    activation_state: "pending_activation" | "active";
  }> =>
    GET(
      APIRoutes.apiSelfSignupActivationStatus,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  /**
   * Self-activation via QR scan — user scans device QR code, we POST the
   * device_id. If the device is valid and unassigned, the backend activates the
   * account, assigns the device, and returns clinic_id.
   *
   * Returns the raw response body; controller is responsible for interpreting
   * success vs error `code` fields.
   */
  const activateDevice = async (
    device_id: string
  ): Promise<{
    activation_state?: string;
    clinic_id?: number;
    device_id?: string;
    code?: string;
    message?: string;
  }> =>
    POST(
      APIRoutes.apiDeviceActivate,
      { device_id },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  return {
    selfSignup,
    checkActivationStatus,
    activateDevice,
  };
};
